import { registerPlugin, Capacitor } from "@capacitor/core";
import { APPLE_PRODUCT_ID } from "@howcomet/shared";

/**
 * The App Store, as seen from JavaScript.
 *
 * Buying only works inside the native app — StoreKit does not exist in a
 * browser — so every call here reports "unavailable" on the web rather than
 * throwing. The paywall uses that to say something useful instead of showing a
 * dead button: a parent reading the grown-ups page in Safari needs to be told
 * to open the app, not shown a spinner.
 */

export interface StoreProduct {
  available: boolean;
  id?: string;
  /** Apple's own localised price string. Preferred over ours when present. */
  price?: string;
  title?: string;
}

export type PurchaseOutcome =
  | { status: "purchased" | "restored"; jws: string }
  /** Ask to Buy: a grown-up has asked and somebody else has to approve. */
  | { status: "pending" }
  | { status: "cancelled" }
  | { status: "nothing" }
  | { status: "unavailable" };

interface StorePlugin {
  product(options: { productId: string }): Promise<StoreProduct>;
  purchase(options: { productId: string; accountToken?: string }): Promise<PurchaseOutcome>;
  restore(): Promise<PurchaseOutcome>;
  addListener(event: "transaction", fn: (data: { jws: string }) => void): Promise<unknown>;
}

const native = registerPlugin<StorePlugin>("HowcometStore");

/** True only where a purchase can actually happen. */
export function canBuy(): boolean {
  return Capacitor.isNativePlatform();
}

export async function storeProduct(): Promise<StoreProduct> {
  if (!canBuy()) return { available: false };
  try {
    return await native.product({ productId: APPLE_PRODUCT_ID });
  } catch {
    return { available: false };
  }
}

/**
 * @param accountToken who to credit, handed to StoreKit so Apple repeats it in
 * every notification about this subscription. Without it the only link between
 * a purchase and a family is the report this device makes afterwards — and a
 * notification arriving first, or a report never arriving, leaves a family that
 * has paid with nothing.
 */
export async function buy(accountToken?: string): Promise<PurchaseOutcome> {
  if (!canBuy()) return { status: "unavailable" };
  return native.purchase({ productId: APPLE_PRODUCT_ID, accountToken });
}

export async function restore(): Promise<PurchaseOutcome> {
  if (!canBuy()) return { status: "unavailable" };
  return native.restore();
}

/**
 * Renewals, refunds and Ask to Buy approvals that arrive while the app is open.
 *
 * Without this, a subscription approved by a second parent minutes after the
 * request leaves the child looking at "Howcomet is having a rest" until someone
 * thinks to press Restore.
 */
export function onTransaction(fn: (jws: string) => void): void {
  if (!canBuy()) return;
  void native.addListener("transaction", (data) => fn(data.jws));
}
